import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Slider } from 'antd';
import { LeadRequest } from '../../../request/lead.request';
import { useLead } from '../lead.hook';
import { LargeStatus } from '../../../model/large-status.model';
import { ItemProp } from '../../../model/item-prop.model';
import { UserRes } from '../../../response/user.response';
import { EmployeeRes } from '../../../response/employee.response';
import { BudgetRange } from '../../../request/enquiry.request';
import { LocationType } from '../../../enum/location-type.enum';
import TextArea from 'antd/es/input/TextArea';
import { ProjectRes } from '../../../response/project.response';
import { SliderTooltipProps } from 'antd/es/slider';
import { EnquirySource } from '../../../enum/source.enum';
import { handleFilter } from '../../../view/input.helper';
import { formatName } from '../../../util/name-helper';

export const AddLeadComponent: React.FC<{ open: boolean, onCancel: () => void }> = ({ open, onCancel }) => {
    const [form] = Form.useForm();
    const { loading, userList, updateLoading, saveLead, fetchEmployeeList, fetchUserList, statusList, employeeList, projectList, fetchProjectList,
        unitTypeList } = useLead()

    const handleOk = () => {
        form.validateFields().then(values => {
            updateLoading(true);
            const leadRequest = {
                clientId: values["clientId"],
                status: {
                    value: values["status"],
                    note: "Enquiry created"
                } as LargeStatus,
                source: EnquirySource.Portal,
                category: values["category"],
                budgetRange: {
                    start: values["budgetRange"][0],
                    end: values["budgetRange"][1]
                } as BudgetRange,
                locality: values["locality"],
                unitType: values["unitType"],
                bhkType: Number(values["bhkType"]),
                city: values["city"],
                state: values["state"],
                ownerId: values["ownerId"],
                location: {
                    type: LocationType.Point,
                    coordinates: [27, 67]
                },
                requirement: values["requirement"],
                projectId: values["projectId"],
                tagList: values["tagList"]
            } as LeadRequest
            saveLead(leadRequest, onSuccess, onFailure)
        });
    }

    const onSuccess = () => {
        updateLoading(false);
        form.resetFields()
        onCancel();
    }

    const onFailure = () => {
        updateLoading(false);
    }

    useEffect(() => {
        fetchEmployeeList()
        fetchUserList()
        fetchProjectList()
    }, [])

    const sliderTooltipProps = {
        formatter: (value: any) => {
            if (value < 1000) {
                return `${value}`
            }
            if (value < 100000) {
                return `${(value / 1000).toFixed(0)}K`
            }
            if (value < 10000000) {
                return `${(value / 100000).toFixed(0)}L`
            }
            return `${(value / 10000000).toFixed(0)}Cr`
        },
        open: true
    } as SliderTooltipProps

    return (
        <Modal
            open={open}
            title="Add New Lead"
            onCancel={onCancel}
            confirmLoading={loading}
            onOk={handleOk}
            okText={"Submit"}
            cancelText={"Cancel"}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="clientId"
                    label="Client Id"
                    rules={[{ required: false, message: 'Please select client' }]}
                >
                    <Select
                        showSearch={true}
                        value='clientId'
                        options={userList.map((user: UserRes) => {
                            return {
                                label: `${formatName(user.userInfo.name)}`,
                                value: user.id
                            } as ItemProp
                        })}
                        filterOption={handleFilter} />
                </Form.Item>
                <Form.Item
                    name="tagList"
                    label="Tags"
                    rules={[{ required: true, message: 'Enter Tags' }]}
                >
                    <Select
                        mode="tags"
                        value='tagList'
                        maxTagCount='responsive'
                        filterOption={handleFilter}
                    />
                </Form.Item>
                <Form.Item
                    name="category"
                    label="Category"
                    rules={[{ required: true, message: 'Please enter category' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    name="requirement"
                    label="Requirement"
                    rules={[{ required: true, message: 'Please enter your requirement' }]}
                >
                    <TextArea />
                </Form.Item>

                <Form.Item
                    name="budgetRange"
                    label="Budget Range"
                    rules={[{ required: false, message: 'Please enter your budget range' }]}
                    initialValue={[20000000, 400000000]}
                >
                    <Slider range min={1000000} max={1000000000} tooltip={sliderTooltipProps} />
                </Form.Item>

                <Form.Item
                    name="locality"
                    label="Locality"
                    rules={[{ required: false }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="unitType"
                    label="Unit Type"
                    rules={[{ required: false }]}

                >
                    <Select
                        value="unitType"
                        options={unitTypeList} 
                        filterOption={handleFilter} />
                </Form.Item>

                <Form.Item
                    name="bhkType"
                    label="BHK"
                    rules={[{ required: false }]}
                >
                    <Input type='number' />
                </Form.Item>

                <Form.Item
                    name="city"
                    label="City"
                    rules={[{ required: false }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="state"
                    label="State"
                    rules={[{ required: false }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true, message: 'status' }]}
                    initialValue={"Pending"}
                >
                    <Select
                        showSearch={true}
                        value='status'
                        options={statusList}
                        filterOption={handleFilter} />
                </Form.Item>

                <Form.Item
                    name="projectId"
                    label="Project"
                    rules={[{ required: true, message: 'Project Id' }]}
                >
                    <Select
                        showSearch={true}
                        value='projectId'
                        options={projectList.map((projectRes: ProjectRes) => {
                            return {
                                label: `${projectRes.name}`,
                                value: projectRes.id
                            } as ItemProp
                        })}
                        filterOption={handleFilter} />
                </Form.Item>

                <Form.Item
                    name="ownerId"
                    label="Owner"
                    rules={[{ required: true, message: 'Owner' }]}
                >
                    <Select
                        showSearch={true}
                        value='ownerId'
                        options={employeeList.map((employee: EmployeeRes) => {
                            return {
                                label: `${formatName(employee.userInfo.name)}`,
                                value: employee.id
                            } as ItemProp
                        })}
                        filterOption={handleFilter} />
                </Form.Item>
            </Form>
        </Modal>
    );
}